import React, { useState } from "react";
import { Loader2, Play, Check, AlertTriangle, Save, ChevronRight, ClipboardList, FileText, User, Home, File, Clapperboard, Camera, RefreshCw, XCircle } from "lucide-react";
import { runTaskToCompletion } from "../../services/taskService";

// 解析 Agent 回复中的 [ACTION:...] 标记
const ACTION_RE = /\[ACTION:(\w+)(?:\|([^\]]+))?\]/;

export function parseAction(text) {
  if (!text) return null;
  const match = text.match(ACTION_RE);
  if (!match) return null;
  const action = match[1];
  const params = {};
  if (match[2]) {
    match[2].split("|").forEach(pair => {
      const [k, v] = pair.split("=", 2);
      if (k && v) params[k.trim()] = v.trim();
    });
  }
  return { action, params, raw: match[0] };
}

export function stripAction(text) {
  if (!text) return text;
  return text.replace(ACTION_RE, "").trim();
}

// 执行 action API
async function executeAction(projectId, action, params) {
  const provider = localStorage.getItem("ai_provider") || "omini";
  const { result } = await runTaskToCompletion(
    "AGENT_ACTION_EXECUTE",
    { projectId: projectId ? Number(projectId) : null, action, params },
    {
      projectId: projectId ? Number(projectId) : null,
      provider,
      source: `agent_action_${action || "unknown"}`,
      timeoutMs: 30 * 60 * 1000,
    }
  );
  return result;
}

// ======================== 交互组件 ========================

// 工作流选择器
function WorkflowChooser({ data, onSelect }) {
  if (!data?.workflows) return null;
  return (
    <div className="mt-2 space-y-1.5">
      <div className="text-2xs font-medium mb-1 inline-flex items-center gap-1" style={{ color: "var(--color-text-muted)" }}>
        <ClipboardList size={11} /> {data.episodeTitle} · {data.scriptLength} 字
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {data.workflows.map(wf => (
          <button
            key={wf.id}
            onClick={() => onSelect(wf.id, wf.name)}
            className="flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg text-left transition-colors hover:bg-[var(--color-bg-tertiary)]"
            style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }}
          >
            <span className="text-xs font-medium" style={{ color: "var(--color-text-primary)" }}>{wf.name}</span>
            <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>{wf.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Stage 结果卡片
function StageResultCard({ data, type }) {
  if (!data) return null;

  if (type === "stage1_result") {
    return (
      <div className="mt-2 rounded-lg px-3 py-2" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }}>
        <div className="text-xs font-medium mb-1.5 inline-flex items-center gap-1" style={{ color: "var(--color-text-primary)" }}><FileText size={12} /> 编剧分析完成</div>
        <div className="flex gap-3 text-2xs" style={{ color: "var(--color-text-muted)" }}>
          <span className="inline-flex items-center gap-1"><User size={10} /> {data.characterCount} 个角色</span>
          <span className="inline-flex items-center gap-1"><Home size={10} /> {data.sceneCount} 个场景</span>
          <span className="inline-flex items-center gap-1"><File size={10} /> {data.segmentCount} 个片段</span>
        </div>
        {data.characters?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {data.characters.map((c, i) => (
              <span key={i} className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>
                {c.name}
              </span>
            ))}
          </div>
        )}
        {data.segments?.length > 0 && (
          <div className="mt-1.5 space-y-0.5">
            {data.segments.slice(0, 5).map((s, i) => (
              <div key={i} className="text-2xs truncate" style={{ color: "var(--color-text-muted)" }}>
                {s.id}. [{s.sceneRef}] {s.rawText}
              </div>
            ))}
            {data.segments.length > 5 && (
              <div className="text-2xs" style={{ color: "var(--color-text-muted)" }}>...还有 {data.segments.length - 5} 个片段</div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (type === "stage2_result") {
    return (
      <div className="mt-2 rounded-lg px-3 py-2" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }}>
        <div className="text-xs font-medium mb-1 inline-flex items-center gap-1" style={{ color: "var(--color-text-primary)" }}><Clapperboard size={12} /> 分镜生成完成</div>
        <div className="flex gap-3 text-2xs" style={{ color: "var(--color-text-muted)" }}>
          <span className="inline-flex items-center gap-1"><Camera size={10} /> {data.shotCount} 个镜头</span>
          <span className="inline-flex items-center gap-1"><RefreshCw size={10} /> 第 {data.iteration} 轮</span>
        </div>
      </div>
    );
  }

  if (type === "stage3_result") {
    const approved = data.approved;
    return (
      <div className="mt-2 rounded-lg px-3 py-2" style={{
        backgroundColor: "var(--color-bg-overlay)",
        border: `1px solid ${approved ? "var(--color-success, #22c55e)" : "var(--color-warning, #f59e0b)"}40`,
      }}>
        <div className="flex items-center gap-1.5 text-xs font-medium mb-1" style={{ color: approved ? "var(--color-success, #22c55e)" : "var(--color-warning, #f59e0b)" }}>
          {approved ? <Check size={14} /> : <AlertTriangle size={14} />}
          {approved ? "导演审核通过" : "导演发现问题"}
        </div>
        <div className="text-2xs" style={{ color: "var(--color-text-muted)" }}>{data.message}</div>
        {data.issues?.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {data.issues.map((issue, i) => (
              <div key={i} className="text-2xs" style={{ color: "var(--color-text-muted)" }}>• {typeof issue === "string" ? issue : JSON.stringify(issue)}</div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (type === "save_storyboard") {
    return (
      <div className="mt-2 rounded-lg px-3 py-2" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-success, #22c55e)40" }}>
        <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: "var(--color-success, #22c55e)" }}>
          <Save size={14} /> 分镜已准备好保存
        </div>
      </div>
    );
  }

  return null;
}

// 确认/执行按钮
function ActionButton({ label, icon, loading, onClick, color }) {
  const Icon = icon || Play;
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-[1.02] disabled:opacity-50"
      style={{
        backgroundColor: `${color || "var(--color-accent-primary)"}15`,
        color: color || "var(--color-accent-primary)",
        border: `1px solid ${color || "var(--color-accent-primary)"}30`,
      }}
    >
      {loading ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />}
      {label}
    </button>
  );
}

// ======================== 主渲染器 ========================

export default function ActionRenderer({ action, projectId, onSendMessage }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [executed, setExecuted] = useState(false);

  const handleExecute = async (actionName, params = {}) => {
    setLoading(true);
    try {
      const res = await executeAction(projectId, actionName || action.action, params);
      setResult(res);
      setExecuted(true);

      // 根据结果类型自动发消息给 Agent
      if (res.success && res.type === "stage1_result") {
        onSendMessage?.(`编剧分析完成了，识别出 ${res.data.characterCount} 个角色、${res.data.sceneCount} 个场景、${res.data.segmentCount} 个片段。请问需要调整吗？还是继续生成分镜？`);
      }
    } catch (e) {
      setResult({ success: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  // 已执行，显示结果
  if (executed && result) {
    if (!result.success) {
      return (
        <div className="mt-2 rounded-lg px-3 py-2 text-xs inline-flex items-center gap-1" style={{ backgroundColor: "var(--color-red-light)", color: "var(--color-error)", border: "1px solid var(--color-error)" }}>
          <XCircle size={12} /> {result.error}
        </div>
      );
    }
    // choose_workflow 执行后显示选择器
    if (result.type === "choose_workflow") {
      return (
        <WorkflowChooser
          data={result.data}
          onSelect={(wfId, wfName) => {
            onSendMessage?.(`使用「${wfName}」模式`);
          }}
        />
      );
    }
    return <StageResultCard data={result.data} type={result.type} />;
  }

  // 未执行，显示交互组件
  switch (action.action) {
    case "choose_workflow":
      return (
        <div>
          {loading && <div className="mt-2 text-2xs" style={{ color: "var(--color-text-muted)" }}><Loader2 size={12} className="inline animate-spin mr-1" />加载中...</div>}
          {!loading && !result && (
            <ActionButton
              label="查看可用模式"
              icon={ChevronRight}
              loading={loading}
              onClick={() => handleExecute("choose_workflow", action.params)}
            />
          )}
          {result?.success && (
            <div className="animate-in fade-in slide-in-from-bottom-1 duration-200">
              <WorkflowChooser
                data={result.data}
                onSelect={(wfId, wfName) => {
                  onSendMessage?.(`使用「${wfName}」模式`);
                }}
              />
            </div>
          )}
        </div>
      );

    case "run_stage1":
      return (
        <ActionButton
          label="开始编剧分析"
          icon={Play}
          loading={loading}
          onClick={() => handleExecute("run_stage1", action.params)}
        />
      );

    case "run_stage2":
      return (
        <ActionButton
          label="开始生成分镜"
          icon={Play}
          loading={loading}
          onClick={() => handleExecute("run_stage2", action.params)}
        />
      );

    case "run_stage3":
      return (
        <ActionButton
          label="开始导演审核"
          icon={Play}
          loading={loading}
          onClick={() => handleExecute("run_stage3", action.params)}
        />
      );

    case "save_storyboard":
      return (
        <ActionButton
          label="保存到分镜列表"
          icon={Save}
          loading={loading}
          onClick={() => handleExecute("save_storyboard", action.params)}
          color="var(--color-success, #22c55e)"
        />
      );

    default:
      return null;
  }
}
